/**
 * Die REST-DTOs — Datenformate der HTTP-Schnittstelle.
 *
 * `CLAUDE.md` Regel 7: die OpenAPI-3.1-Spezifikation wird **nicht nachträglich generiert**,
 * sondern fällt aus genau diesen Schemas heraus, die der Server zur Validierung ohnehin
 * benutzt. Es gibt deshalb keine zweite Quelle, die veralten könnte.
 *
 * Drei Entscheidungen prägen dieses Modul:
 *
 * **1. Der Client schickt eine Konfiguration, keinen Zustand** (Regel 1). Es gibt kein Feld für
 * ein Brett, eine Figurenposition oder einen Punktestand — und weil überall `z.strictObject`
 * steht, wird ein solches Feld abgewiesen statt ignoriert. Ein Test prüft das als Eigenschaft
 * über alle Anfrageschemas.
 *
 * **2. Die Siegbedingung bestimmt die Bedenkzeit** (Nr. 20, Nr. 22), und das ist hier eine
 * **Form**: `gameSettingsSchema` ist eine diskriminierte Union über `victory`. Variante A hat
 * gar kein Feld für eine Bedenkzeit, Variante B verlangt es. Die unmögliche Kombination ist
 * damit nicht sendbar.
 *
 * Das ist **keine** zweite Wahrheit neben `createGameState`: die Engine prüft weiterhin und
 * bleibt die Autorität. Damit die beiden nicht auseinanderlaufen, geht ein Test in
 * `test/rest-dtos.test.ts` mit echt geparsten Konfigurationen durch `createGameState` und prüft,
 * dass die Engine genau die Kombination ablehnt, die das Schema ablehnt (dasselbe Muster wie
 * beim Aktionsschema in Zyklus 7d: ein Vertrag gegen eine abgeschriebene Form ist kein
 * Vertrag).
 *
 * **3. Der Host stellt Minuten ein, der Rechenkern rechnet in Millisekunden.** §42 spricht von
 * Minuten, also stehen hier Minuten. Umgerechnet wird an **einer** Stelle, im Partiedienst —
 * nicht in zwei Schemas und nicht in jeder Route.
 *
 * **Eine Grenze, die beim Erweitern zu beachten ist:** aus diesen Schemas entsteht das
 * OpenAPI-Dokument (`z.toJSONSchema`, über `fastify-type-provider-zod`). Geprüft ist, dass alle
 * hier benutzten Konstrukte sich übersetzen lassen — `.trim()`, `.prefault()`, `.refine()`,
 * diskriminierte Unionen und `union([x, null])`. Ein **`.transform()`**, das die Form wirklich
 * verändert, ist dagegen nicht als JSON Schema darstellbar: zod wirft dann beim Erzeugen der
 * Spezifikation, und zwar erst zur Laufzeit beim Registrieren der Route. Wer so etwas braucht,
 * prüft es vorher mit `z.toJSONSchema(schema)` — oder rechnet im Dienst, nicht im Schema.
 *
 * **Tokens stehen in Antworten, nie in Anfragen.** Ein Token weist sich im Header aus, damit er
 * nicht in Logs, im `Referer` oder in einer URL landet, die jemand weitergibt. Herausgegeben
 * wird er genau einmal: bei der Erzeugung.
 */
import { z } from 'zod';
import { DEBUG_RULE_CODES, PIECE_TYPES, PLAYER_TYPES, SECTOR_IDS, } from '@flaghex/engine';
import { BOT_LEVELS, BOT_MAX_DEPTH, BOT_WEIGHTS_MAX, LEVELS_RESOLVED_AT_START, LLM_NAME_MAX, PURE_STRATEGY_IDS, strategyIdSchema, } from './bots.js';
import { gameEventSchema, TRANSIENT_EVENT_TYPES } from './game-events.js';
import { legalActionSchema } from './legal-actions.js';
import { GAME_STATUSES, gameStateSchema } from './game-state.js';
/** Eine positive ganze Zahl — Minuten, Punktziele, Zählwerte. */
const positiveInt = z.number().int().positive();
/** Die sechs Sektoren, als Liste aus der Engine statt abgeschrieben. */
export const sectorIdSchema = z.enum(SECTOR_IDS);
export const gameStatusSchema = z.enum(GAME_STATUSES);
/**
 * Ein Anzeigename.
 *
 * Der offene Punkt aus Phase 6: abgewiesen wurde bisher nur der leere Name. Drei Grenzen, jede
 * mit einem Grund:
 *
 * - **Leerraum wird abgeschnitten**, damit „Anna" und „Anna " nicht zwei Spieler sind.
 * - **40 Zeichen**, weil Anzeigenamen personenbezogene Daten sind (`CLAUDE.md`) und so wenig
 *   wie möglich gespeichert werden soll. Für einen Vornamen oder Spitznamen reicht es weit.
 * - **Keine Steuerzeichen.** Ein Zeilenumbruch oder ein Nullbyte landet in jeder Lobby-Ansicht
 *   und in jedem Log.
 */
export const displayNameSchema = z
    .string()
    .trim()
    .min(1, 'Ein Spieler braucht einen Anzeigenamen')
    .max(40, 'Der Anzeigename ist zu lang (höchstens 40 Zeichen)')
    // Steuerzeichen **als Escape-Folge**, nicht als Zeichen: ein rohes Steuerzeichen im
    // Quelltext ist unsichtbar, und beim Schreiben dieser Zeile ist genau das passiert — es
    // landete ein Leerzeichen in der Zeichenklasse und hätte „Anna Maria" abgewiesen. Derselbe
    // Befund wie das Nullbyte in einer Testdatei in Zyklus 6d.
    // eslint-disable-next-line no-control-regex -- genau darum geht es hier
    .refine((value) => !/[\u0000-\u001F\u007F]/.test(value), {
    message: 'Der Anzeigename enthält Steuerzeichen',
});
/** Ein Team — ein freier Bezeichner, den der Host vergibt (§8.3, §8.4). */
export const teamIdSchema = z.string().trim().min(1).max(20);
/**
 * Eine Spieler-ID innerhalb einer Partie.
 *
 * Sie entsteht **serverseitig** im Repository (`p1`, `p2`, …) — der Client bekommt sie und gibt
 * sie zurück. Deshalb steht hier bewusst **keine** Mustereinschränkung: die Form der ID an einer
 * zweiten Stelle festzuschreiben würde sie bei der nächsten Änderung still auseinanderlaufen
 * lassen. Geprüft wird nur, was eine Schnittstelle prüfen muss — dass überhaupt etwas von
 * vernünftiger Länge kommt. Ob die ID existiert, weiß ohnehin nur die Datenbank.
 */
export const playerIdSchema = z.string().min(1).max(40);
/**
 * Ein **Farbplatz**: Sektor plus Team, ohne Spieler.
 *
 * `team` fehlt, wenn jeder für sich spielt (§8.1, §8.2). Ungleiche Teams sind erlaubt (Nr. 24),
 * und die Sektorwahl ist frei (Nr. 8) — der Server prüft hier nur die Form.
 */
export const slotConfigSchema = z.strictObject({
    sector: sectorIdSchema,
    team: teamIdSchema.optional(),
});
/**
 * Was in beiden Siegbedingungen gleich ist.
 *
 * `pointTarget` und `resetScoreOnFlagCapture` überschreiben den RuleConfig-Snapshot dieser
 * Partie (§42). Ohne Angabe gilt der globale Default — der Host muss nichts wiederholen, was
 * schon in den globalen Settings steht.
 *
 * `setupTimeMinutes: null` heißt **unbegrenzt**, die erste Option aus §42.
 *
 * **`showCoordinates` fällt aus dieser Reihe heraus — nach außen nicht, nach innen schon.** Für
 * den Host ist es eine Einstellung wie die anderen: er konfiguriert eine Partie und schickt eine
 * Konfiguration (Nr. 27). Gespeichert wird es aber **nicht** im RuleConfig-Snapshot, sondern in
 * einer eigenen Spalte an `games` (entschieden in Zyklus 10g, `ARCHITECTURE.md` **E30**): es ist
 * eine Darstellungsoption, verändert keine Regel und hat deshalb in dem Objekt nichts zu suchen,
 * das die Spiellogik einfriert (§53, `RULE_CONFIG.md`). Dass beide Wege durch dieselbe Form
 * laufen, ist Absicht — wo ein Wert liegt, ist Sache des Servers und nicht der Schnittstelle.
 */
const commonSettings = {
    /** Punktziel (§42). In Variante B wirkungslos, aber nicht falsch (`RULE_CONFIG.md`). */
    pointTarget: positiveInt.optional(),
    /** Aufstellungszeit in Minuten; `null` heißt unbegrenzt (§42). */
    setupTimeMinutes: z.union([positiveInt, z.null()]).optional(),
    /** Score-Reset beim Fähnrich-Schlag im Zentrum (§24, §42). */
    resetScoreOnFlagCapture: z.boolean().optional(),
    /**
     * Feldkoordinaten einblenden (§42, `RULE_CONFIG.md`). Default `false`.
     *
     * Keine Regel, kein Balancewert — und ausdrücklich **kein** Weg zu verdeckter Information:
     * Koordinaten sind öffentlich, die Belegung der Felder in der Aufstellung ist es nicht. Die
     * Player-Perspective-Projektion bleibt unberührt.
     */
    showCoordinates: z.boolean().optional(),
};
/**
 * Die Partie-Einstellungen, als **zwei Formen** statt einer mit optionalen Feldern.
 *
 * Nr. 20 und Nr. 22: die Siegbedingung bestimmt die Uhr. Variante A hat **keine** Bedenkzeit,
 * Variante B **braucht** eine. Als diskriminierte Union ist die unmögliche Kombination nicht
 * einmal sendbar — und ein Client, der sie versucht, bekommt eine Formfehlermeldung statt eines
 * Fehlers aus dem Rechenkern.
 */
export const gameSettingsSchema = z.discriminatedUnion('victory', [
    z.strictObject({ victory: z.literal('POINTS'), ...commonSettings }),
    z.strictObject({
        victory: z.literal('TIME'),
        /** Bedenkzeit **je Spieler** in Minuten (§42). In Variante B erforderlich (Nr. 20). */
        timeBudgetMinutes: positiveInt,
        ...commonSettings,
    }),
]);
/**
 * Dieselben Einstellungen, wie der Dienst sie **herausgibt** — und zwar in den Einheiten, in
 * denen sie gespeichert sind.
 *
 * **Warum nicht dasselbe Schema wie für die Anfrage.** Die Anfrage spricht die Sprache des Hosts:
 * das Formular bietet **Minuten** an, weil niemand eine Aufstellungszeit in Millisekunden eingibt.
 * Gespeichert wird aber, was die RuleConfig führt — `setup.timeLimitMs`, in **Millisekunden**
 * (§42, `RULE_CONFIG.md`). Zwischen beiden liegt eine Division, und die ist nicht verlustfrei:
 * eine Partie **ohne** eigene Angabe erbt den globalen Wert, und 4000 ms sind keine ganzen
 * Minuten.
 *
 * Bis Zyklus 12c rechnete die Lobby-Ansicht trotzdem zurück. Die Folge war kein Rundungsfehler,
 * sondern **500 für jeden Beteiligten**: die Antwort passte nicht mehr zu ihrem eigenen Schema,
 * und zwar bevor die Partie überhaupt beginnen konnte. Gefunden hat das ein Abnahmelauf — kein
 * Servertest hatte je einen globalen Wert gesetzt, der keine ganzen Minuten ist.
 *
 * **Die Lehre, und sie ist allgemein:** eine Einheit, die nur für die Eingabe gewählt wurde, darf
 * nicht die Einheit der Ausgabe bestimmen. Deshalb hier `…Ms` — herausgegeben wird, was dasteht.
 *
 * Alle Felder sind Pflicht: der Dienst kennt jede Einstellung einer bestehenden Partie, ein
 * optionales Feld wäre hier eine Unsicherheit, die es nicht gibt.
 */
const commonSettingsView = {
    pointTarget: positiveInt,
    /** Aufstellungszeit in Millisekunden; `null` heißt unbegrenzt (§42). */
    setupTimeLimitMs: z.union([positiveInt, z.null()]),
    resetScoreOnFlagCapture: z.boolean(),
    showCoordinates: z.boolean(),
};
/**
 * Die Vorgaben, mit denen das Formular des Hosts **vorbelegt** wird (`R10`).
 *
 * Bis `R10` stand das Formular leer, und erst der Dienst füllte die Lücke mit dem globalen
 * Wert. Der Host sah also nicht, was gilt, und trug es in jeder Partie neu ein. Schlimmer: Ein
 * leerer Haken schickte **nichts**, sodass der Reset bei der Vorgabe „an“ für eine Partie gar
 * nicht abschaltbar war (gemeldet vom User, 2026-10-13).
 *
 * **Nur diese zwei Werte, streng:** Die Route ist für jeden gültigen Host-Link lesbar, und mehr
 * als das, was das Formular vorbelegt, geht dort niemanden etwas an.
 */
export const gameDefaultsSchema = z.strictObject({
    pointTarget: positiveInt,
    resetScoreOnFlagCapture: z.boolean(),
});
export const gameSettingsViewSchema = z.discriminatedUnion('victory', [
    z.strictObject({ victory: z.literal('POINTS'), ...commonSettingsView }),
    z.strictObject({
        victory: z.literal('TIME'),
        /** Bedenkzeit **je Spieler** in Millisekunden — dieselbe Begründung wie oben. */
        timeBudgetMs: positiveInt,
        ...commonSettingsView,
    }),
]);
/**
 * Die vollständige Konfiguration einer Partie — Einstellungen und Farbplätze.
 *
 * **Immer vollständig, nie teilweise.** Der Host sieht in seiner Ansicht eine Konfiguration und
 * schickt eine Konfiguration (Nr. 27). Eine API aus Einzelschritten würde Zwischenzustände
 * erzeugen, die er nie gewollt hat — „4 Spieler, aber noch die Teams von 6".
 */
export const gameConfigurationSchema = z.strictObject({
    settings: gameSettingsSchema,
    slots: z
        .array(slotConfigSchema)
        .min(1, 'Eine Partie braucht mindestens einen Farbplatz')
        .max(SECTOR_IDS.length)
        .refine((slots) => new Set(slots.map((slot) => slot.sector)).size === slots.length, { message: 'Jeder Sektor darf nur einmal vorkommen' }),
});
/**
 * Die Partieerstellung.
 *
 * Der Host gibt seinen Anzeigenamen und seine Farbe mit an: er ist **Spieler Nummer eins**
 * (Nr. 27), und eine Partie hat damit von ihrem ersten Moment an einen zugeordneten Spieler.
 * Dass seine Farbe ein konfigurierter Platz sein muss, prüft das Schema — beides steht in
 * derselben Anfrage, es ist also Stimmigkeit der Form und keine Spielregel.
 */
export const createGameRequestSchema = gameConfigurationSchema
    .extend({
    host: z.strictObject({
        displayName: displayNameSchema,
        sector: sectorIdSchema,
    }),
})
    .refine((request) => request.slots.some((slot) => slot.sector === request.host.sector), {
    message: 'Die Farbe des Hosts muss ein konfigurierter Farbplatz sein',
    path: ['host', 'sector'],
});
/**
 * Die Antwort auf die Partieerstellung.
 *
 * Beide Geheimnisse verlassen den Server genau **einmal**: der Invite-Token, damit der Host den
 * Link bauen kann, und der Sitzungs-Token für seinen eigenen Platz. Danach sind sie nicht mehr
 * herstellbar — gespeichert ist nur ihr Hash.
 */
export const createGameResponseSchema = z.strictObject({
    gameId: z.string().uuid(),
    inviteToken: z.string().min(1),
    playerId: z.string().min(1),
    sessionToken: z.string().min(1),
});
/**
 * Der Beitritt über den Einladungslink (§44).
 *
 * Der Invite-Token steht **nicht** hier, sondern im Header: in einem Body landet er leichter in
 * einem Log, und in einer URL im `Referer`.
 *
 * `sector` ist optional (Nr. 29) — wer keine Farbe wählt, wartet, bis er wählt oder der Host
 * ihn zuordnet.
 */
export const joinRequestSchema = z.strictObject({
    displayName: displayNameSchema,
    sector: sectorIdSchema.optional(),
});
export const joinResponseSchema = z.strictObject({
    gameId: z.string().uuid(),
    playerId: z.string().min(1),
    sessionToken: z.string().min(1),
});
/**
 * Der Host ordnet zu, setzt um oder löst (Nr. 29).
 *
 * `sector: null` ist **ausdrücklich** und nicht weggelassen: ein fehlendes Feld wäre zweideutig
 * — „nicht ändern" oder „lösen"? Der Host meint eines von beiden, und Raten ist hier falsch.
 */
export const assignSectorRequestSchema = z.strictObject({
    playerId: playerIdSchema,
    sector: z.union([sectorIdSchema, z.null()]),
});
/** Ein Name eines Gewichts oder Anbieters — kurz, nicht leer. */
const kurzerName = z.string().min(1).max(LLM_NAME_MAX);
/**
 * Die Gewichte einer Wahl: Name → Zahl, begrenzt in der Zahl. **Endlich ohne `.finite()`**:
 * `z.number()` weist in zod 4 `NaN` und Unendlich selbst ab (geprüft in `L1c2`).
 */
const weightsChoiceSchema = z
    .record(kurzerName, z.number())
    .refine((gewichte) => Object.keys(gewichte).length <= BOT_WEIGHTS_MAX, {
    message: `höchstens ${BOT_WEIGHTS_MAX} Gewichte`,
});
/**
 * Was der Host für einen Bot **wählt** (`L1c2`, **E104**) — eine Stufe, eine Strategie mit Tiefe
 * und Gewichten („genauer"), oder ein Sprachmodell.
 *
 * **Eine Wahl und keine Definition, und das ist der Sicherheitsbefund vom 2026-10-11** (**E105**):
 * bis `L1c2` nahm die Route eine ganze `BotDefinition` an, mit Ausführungsart und Budget — wer sie
 * direkt rief, setzte `maxDepth: 50`. Seitdem setzt **der Dienst** beides
 * (`apps/server/src/bots/levels.ts`, **E106**), und die Anfrage **kann** es nicht tragen
 * (`strictObject` je Zweig).
 *
 * **Die Tiefe ist im Vertrag begrenzt** (`BOT_MAX_DEPTH`, gemessen) und damit in der
 * OpenAPI-Beschreibung sichtbar; ob eine Strategie eine Tiefe kennt, ob ein Gewicht existiert und
 * ob ein Anbieter eingerichtet ist, prüft der Dienst (422) — der Vertrag kennt die Strategien
 * nicht. `llm` ist kein Zweig von `STRATEGY`: ein Sprachmodell hat einen Anbieter und ein Modell,
 * keine Tiefe und keine Gewichte.
 */
export const botChoiceSchema = z.discriminatedUnion('kind', [
    z.strictObject({ kind: z.literal('LEVEL'), level: z.enum(BOT_LEVELS) }),
    z.strictObject({
        kind: z.literal('STRATEGY'),
        strategy: z.enum(PURE_STRATEGY_IDS),
        depth: z.number().int().min(1).max(BOT_MAX_DEPTH).optional(),
        weights: weightsChoiceSchema.optional(),
    }),
    z.strictObject({ kind: z.literal('LLM'), providerId: kurzerName, modelId: kurzerName }),
]);
/**
 * Einen Bot auf einen freien Farbplatz setzen (`V2-2f`, **E64**; seit `L1c2` mit einer **Wahl**).
 *
 * **Der Farbplatz ist Pflicht, anders als beim Beitritt eines Menschen** (Nr. 29). Ein Mensch
 * kommt an, bevor der Host über die Plätze entschieden hat, und darf selbst wählen — ein Bot
 * hat beides nicht: ihn setzt der Host, zu einem Zeitpunkt seiner Wahl, und er weiß wohin. Ein
 * Bot ohne Farbe wäre ein Wartender, der auf niemanden wartet, und er zählte trotzdem gegen die
 * Kapazität der Lobby. Lösen lässt er sich später wie ein Mensch (`assignSector`) — *entstehen*
 * kann er so nicht.
 *
 * **Den Namen tippt der Host** (**E64**) und er wird nicht aus der Farbe erzeugt: nur so sind
 * zwei Konfigurationen derselben Strategie unterscheidbar (`V2 aggressiv` gegen `V1 flach`), und
 * genau das ist die Größe, die das Balancing-Labor später vergleicht.
 */
export const addBotRequestSchema = z.strictObject({
    displayName: displayNameSchema,
    sector: sectorIdSchema,
    choice: botChoiceSchema,
});
/**
 * Die Antwort darauf — **ohne Sitzungs-Token**, und das ist die Zusage (**E60**).
 *
 * Ein Bot hat nichts nachzuweisen; die Datenbank verbietet ihm ein Token sogar (**E59**, drei
 * `CHECK`-Bedingungen). `joinResponseSchema` gibt einen heraus, dieses Schema *kann* es nicht —
 * `strictObject` weist das Feld ab, statt es durchzulassen.
 */
export const addBotResponseSchema = z.strictObject({
    gameId: z.string().uuid(),
    playerId: z.string().min(1),
});
/**
 * Was die Mitspieler über einen Bot erfahren (`L1c2`, **E104** Teilfrage 3) — **eine
 * Positivliste**: Stufe, Strategie, wirksame Tiefe, Gewichte, beim LLM Anbieter und Modell.
 *
 * **Nie die Definition.** `config` ist ein offenes JSON-Objekt (beim LLM mit Frist, Versuchen und
 * Skills), `execution` und `budget` gehören dem Dienst. Jeder Zweig ist ein `strictObject`: was
 * nicht in der Liste steht, kann nicht abfließen — die Begründung aus `V2-2f1`, nur dass die Liste
 * seit E104 nicht mehr leer ist.
 *
 * **Zwei Formen:** eine **offene** Stufe (`strong` vor dem Start, **E107**) nennt nur sich selbst
 * — welche Strategie sie wird, hängt an der Zahl der Seiten beim Start; jeder andere Bot nennt
 * seine Strategie, und die Stufe nur, **wenn** seine Einstellung ihr gleicht (Teilfrage 5:
 * berechnet, nicht gemerkt).
 */
export const lobbyBotSchema = z.union([
    z.strictObject({
        level: z.enum(LEVELS_RESOLVED_AT_START),
        resolvesAtStart: z.literal(true),
    }),
    z.strictObject({
        level: z.enum(BOT_LEVELS).optional(),
        strategy: strategyIdSchema,
        depth: z.number().int().min(1).optional(),
        weights: z.record(z.string(), z.number()).optional(),
        model: z.strictObject({ providerId: z.string().min(1), modelId: z.string().min(1) }).optional(),
    }),
]);
/**
 * Ein Spieler in der Lobby. **Ohne** Sitzungs-Token — die Ansicht geht an alle.
 *
 * **Und ohne Bot-Definition** (`V2-2f1`): `playerType` sagt, *wer* steuert; *wie*, sagt seit
 * `L1c2` nur die Positivliste in `bot` (**E104**). Die Definition liegt in `game_players.bot`
 * und bleibt dort.
 */
export const lobbyPlayerSchema = z.strictObject({
    playerId: z.string().min(1),
    displayName: displayNameSchema,
    /** Ob gerade eine Verbindung besteht (§45, §48). Ein Platz bleibt belegt, wenn nicht. */
    connected: z.boolean(),
    /**
     * Wer diesen Platz steuert (§3, **E53**) — die Lobby zeigt daran das Abzeichen `[Bot]`
     * (**E64**) und lässt „nicht verbunden" bei einem Bot weg.
     *
     * **Pflichtfeld, keine Vorbelegung.** Dieselbe Entscheidung wie bei `deriveState` in
     * `V2-2d`: wer `'HUMAN'` erfindet, wo die Angabe fehlt, macht einen Bot stillschweigend zum
     * Menschen — und weil die Lobby dann nur das Abzeichen weglässt, fällt es niemandem auf.
     */
    playerType: z.enum(PLAYER_TYPES),
    /** Nur bei einem Bot (`L1c2`) — die Positivliste aus `lobbyBotSchema`. */
    bot: lobbyBotSchema.optional(),
});
/** Ein Farbplatz in der Lobby — mit dem Spieler, der darauf sitzt, falls einer sitzt. */
export const lobbySlotSchema = slotConfigSchema.extend({
    player: lobbyPlayerSchema.optional(),
});
/**
 * Die Lobby-Ansicht.
 *
 * **Eine Form für zwei Ansichten** (Nr. 27): die Host-Ansicht ist Konfiguration und Lobby in
 * einem, die Spieler-Lobby zeigt dasselbe ohne die Bedienelemente. Unterschieden wird nicht am
 * Datenformat, sondern an den Routen — was eine Antwort nicht enthält, kann nicht abfließen.
 *
 * **Kein Einladungslink hier**, und das ist eine Folge, keine Auslassung: der Invite-Token liegt
 * nur **gehasht** in der Datenbank (`CLAUDE.md`, Security by Design). Sein Klartext existiert
 * genau einmal — in der Antwort auf die Partieerstellung. Verliert der Host ihn, erzeugt er
 * einen neuen; der alte verfällt dabei. Ein Feld, das der Server nicht füllen *kann*, wäre eine
 * Einladung, es doch im Klartext zu speichern.
 *
 * `canStart` kommt vom Server und wird **nicht** im Client gerechnet: die Bedingung ist eine
 * Regel (Nr. 28, jeder Platz belegt), und Regeln entscheidet der Server (Regel 1). Der Client
 * darf anzeigen, was möglich ist — nie, was gültig ist.
 */
export const lobbyViewSchema = z.strictObject({
    gameId: z.string().uuid(),
    status: gameStatusSchema,
    settings: gameSettingsViewSchema,
    slots: z.array(lobbySlotSchema),
    /** In der Lobby, aber keiner Farbe zugeordnet (Nr. 29). */
    waiting: z.array(lobbyPlayerSchema),
    /**
     * Nr. 28 **und** Nr. 24: wahr, sobald jeder konfigurierte Farbplatz belegt ist und
     * mindestens zwei Seiten übrigbleiben — sonst hätte niemand einen Gegner.
     */
    canStart: z.boolean(),
});
/** Ein Host-Link anlegen. Das Label macht nachvollziehbar, wer welchen Link bekam. */
export const hostTokenCreateRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(60),
});
/**
 * Ein Host-Link, wie ihn die Admin-Liste zeigt — **ohne** Token.
 *
 * Es gibt hier gar kein Feld dafür: der Klartext existiert einmal, bei der Erzeugung. Was nicht
 * herausgegeben wird, kann nicht versehentlich in eine Antwort oder ein Log geraten.
 */
export const hostTokenSchema = z.strictObject({
    id: z.string().uuid(),
    label: z.string(),
    createdAt: z.string(),
    revokedAt: z.string().optional(),
    lastUsedAt: z.string().optional(),
});
/** Die Antwort beim Anlegen — hier **mit** Token, und nur hier. */
export const hostTokenCreatedSchema = hostTokenSchema.extend({
    token: z.string().min(1),
});
/** Die Admin-Liste der Host-Links. */
export const hostTokenListSchema = z.array(hostTokenSchema);
/**
 * Ein Verlagsschlüssel anlegen (`W1`, **E135**). Der Name sagt, welcher Verlag ihn bekam.
 *
 * Dieselbe Form wie ein Host-Link, aber eine **eigene** Berechtigung: er öffnet die Anleitung samt
 * Erklärfilm und sonst nichts.
 */
export const publisherKeyCreateRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(60),
});
/** Eine Zahl, die die Anleitung meldet: Sekunden im Film oder eine Kapitelnummer. Zehn Stunden Luft. */
const manualCount = z.number().int().min(0).max(36_000);
/**
 * Was die Online-Anleitung meldet (`W3`, **E136**) — nur diese vier Arten, nur Zahlen. Freischalten
 * und Öffnen zählt der Dienst selbst; die kann die Seite nicht melden.
 */
export const manualClientEventKindSchema = z.enum(['FILM_START', 'FILM_PROGRESS', 'FILM_CHAPTER', 'READ_PROGRESS']);
export const manualEventRequestSchema = z.strictObject({
    kind: manualClientEventKindSchema,
    /** Sekunden im Film (`FILM_PROGRESS`, `FILM_CHAPTER`) oder die Kapitelnummer (`READ_PROGRESS`). */
    value: manualCount.optional(),
    /** Die Länge des Films oder die höchste Kapitelnummer. */
    of: manualCount.min(1).optional(),
});
/** Alle Arten im Verlauf: die zwei, die der Dienst zählt, und die vier gemeldeten. */
export const manualEventKindSchema = z.enum([
    'UNLOCK',
    'OPEN',
    'FILM_START',
    'FILM_PROGRESS',
    'FILM_CHAPTER',
    'READ_PROGRESS',
]);
/** Ein Eintrag im Verlauf eines Verlagsschlüssels — ohne IP, Browser oder Gerät. */
export const publisherKeyEventSchema = z.strictObject({
    kind: manualEventKindSchema,
    value: manualCount.optional(),
    of: manualCount.optional(),
    at: z.string(),
});
export const publisherKeyEventListSchema = z.array(publisherKeyEventSchema);
/**
 * Was ein Verlag mit der Anleitung getan hat, zusammengefasst (`W3`, **E136**). „Bis“ ist jeweils die
 * größte gemeldete Stelle; ein Feld fehlt, solange es nichts dazu gibt.
 */
export const publisherKeyActivitySchema = z.strictObject({
    unlocks: z.number().int().min(0),
    lastUnlockAt: z.string().optional(),
    opens: z.number().int().min(0),
    lastOpenAt: z.string().optional(),
    filmStarts: z.number().int().min(0),
    filmFurthest: manualCount.optional(),
    filmLength: manualCount.optional(),
    readFurthest: manualCount.optional(),
    readOf: manualCount.optional(),
});
/** Ein Verlagsschlüssel, wie ihn die Admin-Liste zeigt — **ohne** den Schlüssel selbst. */
export const publisherKeySchema = z.strictObject({
    id: z.string().uuid(),
    label: z.string(),
    createdAt: z.string(),
    revokedAt: z.string().optional(),
    lastUsedAt: z.string().optional(),
    activity: publisherKeyActivitySchema,
});
/** Die Antwort beim Anlegen — hier **mit** Schlüssel, und nur hier. */
export const publisherKeyCreatedSchema = publisherKeySchema.extend({
    key: z.string().min(1),
});
export const publisherKeyListSchema = z.array(publisherKeySchema);
/**
 * Die Anleitung freischalten. Der Schlüssel reist im Körper einer `POST`-Anfrage, nie in einer
 * Adresse; die Länge ist nur eine Obergrenze gegen Müll, kein Formurteil.
 */
export const manualUnlockRequestSchema = z.strictObject({
    key: z.string().trim().min(1).max(200),
});
const euro = z.number().min(0);
/**
 * Der Stand der LLM-Kosten (`V2-9d2`, **E88**, **E92**) — was der Admin sieht, bevor und nachdem
 * ein Modell spielt. Der Rauchlauf in `V2-9e` liest hier ab, was er gekostet hat.
 *
 * **Geschlossen** (`strictObject`): ein Schlüssel hat hier keinen Platz (§121), und die Preise
 * erscheinen nur als **Liste der Modelle**, für die es einen gibt — wer wissen will, warum ein Bot
 * `unpriced` meldet, sieht hier, dass sein Modell fehlt.
 */
export const llmStatusSchema = z.strictObject({
    /**
     * Der Not-Aus aus zwei Quellen: die **Umgebung** (`FLAGHEX_LLM_KILL`, beim Start) und der
     * **Admin** (gespeichert, überlebt einen Neustart). Aktiv ist er, sobald eine davon es sagt.
     */
    killSwitch: z.strictObject({
        environment: z.boolean(),
        admin: z.boolean(),
        active: z.boolean(),
    }),
    caps: z.strictObject({ perGameEur: euro, perDayEur: euro }),
    /** Der Kalendertag in `Europe/Berlin` und was an ihm gebucht ist, offene Reservierungen inklusive. */
    today: z.strictObject({
        day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u),
        spentEur: euro,
    }),
    /** `provider/modell`, nach Namen geordnet. */
    pricedModels: z.array(z.string().min(1)),
});
/**
 * Die Sprachmodelle, die ein Host für einen Bot wählen kann (`L1d`, **E104** Teilfrage 4) —
 * `GET /api/v1/llm/models`, **öffentlich**.
 *
 * **Nur Kennungen.** Nie ein Preis (er verriete die Abrechnung), nie ein Schlüssel oder eine
 * Basis-URL (Regel 8, **E88**), nie der Tagesstand (der gehört dem Admin, `llmStatusSchema`).
 * Jede Ebene ist ein `strictObject`: was nicht hier steht, kann nicht abfließen.
 *
 * **Die Deckel stehen dabei** — sie sind der Kostenhinweis, den E104 neben der Modellwahl
 * verlangt („höchstens 2 € je Partie"), und kein Geheimnis.
 */
export const llmModelsSchema = z.strictObject({
    /** Nur, was der Dienst **fragen kann**: Anbieter eingerichtet, Preis bekannt, Not-Aus aus. */
    models: z.array(z.strictObject({ providerId: z.string().min(1), modelId: z.string().min(1) })),
    caps: z.strictObject({ perGameEur: euro, perDayEur: euro }),
});
/** Den Not-Aus des Admins setzen — an oder aus, nichts sonst. */
export const llmKillSwitchRequestSchema = z.strictObject({ active: z.boolean() });
/**
 * Ein Pfadparameter, der eine ID trägt (`/host-tokens/:id`).
 *
 * Auch das gehört hierher und nicht in den Server: **alle** Datenformate liegen in diesem
 * Paket (Regel 7), sonst entstünde im Server eine zweite Stelle, an der Formen definiert
 * werden — und die OpenAPI-Spezifikation beschriebe sie nicht mit.
 */
export const idParamsSchema = z.strictObject({ id: z.uuid() });
/**
 * Eine Partie-ID.
 *
 * **Die Partie-ID darf in der URL stehen** — sie ist kein Geheimnis, sondern nur eine Adresse.
 * Was in der URL nichts zu suchen hat, sind Token (Logs, `Referer`, weitergeleitete Links);
 * die stehen in Headern.
 *
 * Steht einzeln hier, weil der Host-Wächter sie **vor** der Schemavalidierung einer Route
 * braucht: ohne diese Prüfung ginge eine ID wie `abc` als `uuid` an die Datenbank und käme als
 * Serverfehler zurück statt als Formfehler.
 */
export const gameIdSchema = z.uuid();
/** `/games/:gameId/...` */
export const gameParamsSchema = z.strictObject({ gameId: gameIdSchema });
/** `/games/:gameId/players/:playerId` */
export const gamePlayerParamsSchema = z.strictObject({
    gameId: gameIdSchema,
    playerId: playerIdSchema,
});
/**
 * Ein neuer Einladungslink.
 *
 * Die zweite und letzte Stelle, an der ein Invite-Token den Server verlässt (die erste ist die
 * Partieerstellung). Gespeichert ist nur sein Hash, der alte verfällt dabei.
 */
export const inviteTokenSchema = z.strictObject({ inviteToken: z.string().min(1) });
/** Eine Antwort ohne Inhalt (204). */
export const noContentSchema = z.null();
/**
 * Die Antwort des Gesundheitsendpunkts.
 *
 * **Auch das gehört hierher** (Regel 7): sobald ein Client eine Antwort auswertet, ist ihre Form
 * ein Vertrag — und zwei Stellen, die ihn beschreiben, laufen auseinander. Vor Zyklus 10a wurde
 * `/api/v1/health` von niemandem gelesen und hatte deshalb kein Schema; es stand damit auch ohne
 * Antwortform in der OpenAPI-Spezifikation.
 *
 * `literal('ok')` und nicht `string`: der Endpunkt antwortet entweder so oder gar nicht. Ein
 * freier String hätte dem Client erlaubt, einen Dienst in Schieflage als gesund zu lesen.
 */
export const healthSchema = z.strictObject({ status: z.literal('ok') });
/**
 * Die Fehlercodes der HTTP-Schnittstelle.
 *
 * Getrennte Codes für Fälle, die der Aufrufer **verschieden behandeln muss** — dieselbe
 * Begründung wie bei den zwei Beitrittsfehlern im Repository: „belegt" heißt wähle eine andere
 * Farbe, „voll" heißt es gibt nichts zu wählen, „nicht konfiguriert" heißt diese Partie benutzt
 * diese Farbe nicht (Nr. 29, Nr. 30). Ein gemeinsamer Fehler wäre in der Lobby nicht auflösbar.
 */
export const API_ERROR_CODES = [
    /** Die Anfrage passt nicht auf das Schema. */
    'INVALID_REQUEST',
    /** Kein oder kein gültiger Token. Schlägt **geschlossen** fehl: kein Hinweis, was fehlte. */
    'UNAUTHENTICATED',
    /** Token gültig, aber nicht für diese Handlung. */
    'FORBIDDEN',
    'NOT_FOUND',
    /** Dieser Host-Token hat schon eine aktive Partie (`CLAUDE.md`, Berechtigungsmodell). */
    'ACTIVE_GAME_EXISTS',
    'LOBBY_FULL',
    'SECTOR_TAKEN',
    'SLOT_NOT_CONFIGURED',
    /** Die Partie ist in einem Zustand, in dem das nicht geht — etwa Beitritt nach dem Start. */
    'WRONG_STATE',
    /** Die Konfiguration ist formal gültig, aber als Partie unspielbar (Nr. 20, Nr. 24, §9). */
    'INVALID_CONFIGURATION',
    /**
     * **Strukturell unmöglich** — Klasse C aus Nr. 38, nie mit `force` erreichbar.
     *
     * Zwei Figuren auf einem Feld, eine Koordinate, die kein Feld ist, eine Figur, die nicht auf
     * dem Brett steht. Eigener Code neben `RULE_VIOLATION`, weil der Unterschied für den
     * Aufrufer wesentlich ist: das eine kann er mit „trotzdem" wiederholen, das andere nie.
     */
    'IMPOSSIBLE_POSITION',
    /**
     * **Regelwidrig, aber strukturell sauber** — Klasse B aus Nr. 38.
     *
     * Standardmäßig abgewiesen, und die Meldung nennt die verletzte Regel. Mit `force` wird der
     * Eingriff angenommen; die Partie trägt danach dauerhaft „regelwidrig erzeugt" (**E39**).
     */
    'RULE_VIOLATION',
    'RATE_LIMITED',
    'INTERNAL',
];
/**
 * Eine Fehlerantwort.
 *
 * Bewusst klein: ein Code, den der Client auswerten kann, und eine Meldung für Menschen. **Kein
 * Stacktrace, keine Constraint-Namen, keine zod-Pfade** — die beschreiben die innere Form des
 * Servers, und das muss ein Fremder nicht erfahren (dieselbe Entscheidung wie bei
 * `INVALID_MESSAGE` im WebSocket-Dispatch).
 */
export const apiErrorSchema = z.strictObject({
    code: z.enum(API_ERROR_CODES),
    message: z.string().min(1),
});
/**
 * Die **lesende Debug-Ansicht** einer Partie (§60, Zyklus 11b).
 *
 * Eine Antwort für vier Punkte aus §60 — „GameState inspizieren", „RuleConfig der Partie
 * ansehen", „Figurenpositionen ansehen", „Zughistorie ansehen". Zusammengelegt, weil sie
 * dieselbe Partie beschreiben: vier Routen wären vier Rundreisen für ein Bild, das nur
 * zusammen stimmt.
 *
 * **Diese Ansicht ist unprojiziert.** In `SETUP` enthält sie die geheimen Aufstellungen aller
 * Spieler (§45 filtert sie für Spieler heraus) — das ist ihr Zweck und der Grund, warum sie
 * zwei Bedingungen hinter sich hat: `ADMIN_KEY` **und** den ausdrücklichen Debug-Schalter.
 * Ohne beides wird der Zweig nicht registriert, und dieses Schema erscheint auch nicht in der
 * Spezifikation.
 *
 * **Figurenpositionen haben kein eigenes Feld.** Sie stehen in `state.boardState.pieces`; eine
 * zweite Liste wäre eine zweite Wahrheit, die abweichen kann. Dass §60 sie getrennt aufzählt,
 * beschreibt eine Ansicht der Oberfläche, keine zweite Datenquelle.
 */
export const debugProvenanceSchema = z.strictObject({
    /** Als Sandkiste angelegt (`ARCHITECTURE.md` **E39**). */
    debugGame: z.boolean(),
    /** Wann der erste schreibende Eingriff stattfand — fehlt, wenn keiner stattfand. */
    debugTouchedAt: z.string().optional(),
    /** Wann zuerst eine Regel übergangen wurde (Regelprotokoll Nr. 38). */
    ruleOverriddenAt: z.string().optional(),
    /** Aus den drei Markierungen abgeleitet — die Frage, die ab V3 zählt. */
    countsForBalancing: z.boolean(),
    /** Der Seed des PRNG. Ohne ihn ist die Partie nicht nachrechenbar (§128). */
    seed: z.string(),
    /** Engine-Version plus Build-Bezeichner (**E18**, **E21**). */
    engineVersion: z.string(),
    /** Fehlt bei einer Partie, die ohne Host-Link entstand. Nur die **ID**, nie ein Token. */
    hostTokenId: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Woher der gezeigte Zustand kommt.
 *
 * Nicht Beiwerk, sondern die Antwort auf eine Falle: der autoritative Zustand einer laufenden
 * Partie liegt im **Speicher** (`ARCHITECTURE.md` **E9**), in der Datenbank steht der letzte
 * Commit. Eine Ansicht aus der Datenbank zeigte die laufende Preview nie (§41 — sie wird
 * absichtlich nicht persistiert) und wäre mitten im Zug still veraltet. Wer die Ansicht liest,
 * muss wissen, welchen der beiden Fälle er vor sich hat.
 */
export const debugStateSourceSchema = z.enum(['MEMORY', 'DATABASE']);
export const debugGameViewSchema = z.strictObject({
    id: gameIdSchema,
    stateSource: debugStateSourceSchema,
    provenance: debugProvenanceSchema,
    /** Der **vollständige** Zustand, samt RuleConfig-Snapshot der Partie (§53, Regel 3). */
    state: gameStateSchema,
    /** Die vollständige permanente Historie (§56) — ungefiltert, in Reihenfolge der `seq`. */
    events: z.array(gameEventSchema),
    /**
     * Die legalen Aktionen des Spielers **am Zug** (Zyklus 11g).
     *
     * **Ohne sie ist „Spieler simulieren" nicht bedienbar:** der Eingriff nimmt eine `actionId`,
     * und die kennt nur die Engine. Ein Mitspieler bekommt die Aktionen eines anderen nie zu
     * sehen (die Projektion zeigt sie ihm nicht), also muss diese Ansicht sie führen.
     *
     * **Nur die des Spielers am Zug**, weil nur er ziehen kann (§50) — wer einen anderen ziehen
     * lassen will, setzt erst die aktive Person. **Leer statt fehlend**, wenn niemand am Zug ist:
     * ein fehlendes Feld wäre von „die Fassung kannte es noch nicht" nicht zu unterscheiden.
     */
    legalActions: z.array(legalActionSchema),
});
/**
 * Ein Eintrag im **Preview-Mitschnitt** des Debug-Modus (§41, §57, Zyklus 11c).
 *
 * `actionId` fehlt bei `PREVIEW_CANCELLED` — dort gibt es keine erwogene Aktion mehr. Ein
 * **Commit** erscheint hier nicht: §57 nennt drei Typen, und „bestätigt" ist keiner davon. Wer
 * sehen will, was nach drei erwogenen Zügen gespielt wurde, liest die permanente Historie dazu
 * und verbindet beide über `turnNumber`.
 */
export const debugPreviewEntrySchema = z.strictObject({
    /** Fortlaufend je Partie. Zählt die **Ereignisse**, nicht die Liste — siehe `dropped`. */
    seq: z.number().int().min(1),
    type: z.enum(TRANSIENT_EVENT_TYPES),
    player: playerIdSchema,
    actionId: z.string().min(1).optional(),
    turnNumber: z.number().int().min(0),
    /** Wann, als ISO-Text. */
    at: z.string(),
});
/**
 * Der Mitschnitt einer Partie — **begrenzt und flüchtig.**
 *
 * `capacity` und `dropped` stehen in **jeder** Antwort und nicht nur in einer vollen: ohne sie
 * wäre ein abgeschnittener Mitschnitt von einem kurzen nicht zu unterscheiden, und man hielte drei
 * Previews für alles, was erwogen wurde. Beim Balancing ist das genau der falsche Schluss.
 *
 * Nichts davon wird persistiert (§41): ein Serverneustart verliert den Mitschnitt, und das ist
 * beabsichtigt — rekonstruierbar bleibt nur, was dieser Prozess noch im Speicher hält.
 */
export const debugPreviewLogSchema = z.strictObject({
    gameId: gameIdSchema,
    capacity: z.number().int().min(1),
    dropped: z.number().int().min(0),
    entries: z.array(debugPreviewEntrySchema),
});
/** Eine Axialkoordinate. Wie in `ws-messages.ts`: zwei ganze Zahlen, nichts weiter (**E15**). */
const hexSchema = z.strictObject({ q: z.number().int(), r: z.number().int() });
/**
 * Ein **schreibender** Debug-Eingriff — §60, Zyklus 11d.
 *
 * **Eine Absicht, kein Zustand** (Regel 1). Auch die Cheat-API sagt „setze *diese* Figur auf
 * *dieses* Feld" und nicht „das Brett sieht jetzt so aus". Der Unterschied ist hier wichtiger
 * als überall sonst: ein Zustandsfeld in dieser Anfrage wäre der einzige ungeprüfte Weg in den
 * `BoardState` — und läge in der einen Route, die ausdrücklich Regeln brechen darf.
 *
 * **Ein geschlossener Satz.** Eine unterschiedene Vereinigung über `op`, nicht ein Objekt mit
 * vielen optionalen Feldern: so ist jede Form für sich vollständig, und ein Eingriff ohne seine
 * Pflichtangaben ist nicht einmal sendbar. Die Gruppe „Verändern" wächst hier um je einen Zweig
 * (11e bis 11g).
 *
 * **`force` ist das ausdrückliche „trotzdem" aus Nr. 38** und hat bewusst **keinen** Default:
 * ein `force: false` im geparsten Ergebnis würde aus einer Entscheidung eine Einstellung
 * machen. Es hebt nur Klasse B auf — Klasse C bleibt abgewiesen.
 */
export const debugInterventionSchema = z.discriminatedUnion('op', [
    z.strictObject({
        op: z.literal('MOVE_PIECE'),
        pieceId: z.string().min(1),
        to: hexSchema,
        force: z.boolean().optional(),
    }),
    /**
     * Eine Figur **ganz** aus dem Spiel nehmen — vom Brett oder aus dem Pool (Zyklus 11e).
     *
     * Klasse B nach Nr. 42, solange der Figurensatz dadurch vom Snapshot abweicht: im Spiel ist
     * eine Figur nie fort, ein Schlag legt sie in den Pool (§22).
     */
    z.strictObject({
        op: z.literal('REMOVE_PIECE'),
        pieceId: z.string().min(1),
        force: z.boolean().optional(),
    }),
    /**
     * Eine **neue** Figur erzeugen und aufs Brett setzen (Zyklus 11e).
     *
     * **Ohne Kennung**: die vergibt die Engine. Eine von außen gewählte wäre der einzige Weg, zwei
     * Figuren mit derselben ID zu erzeugen — eine Konsistenzverletzung, die kein `force` je
     * erlauben darf (Nr. 38).
     */
    z.strictObject({
        op: z.literal('ADD_PIECE'),
        owner: playerIdSchema,
        type: z.enum(PIECE_TYPES),
        to: hexSchema,
        force: z.boolean().optional(),
    }),
    /**
     * Eine Figur vom Brett in den **Respawn-Pool** legen (Zyklus 11e).
     *
     * **Ohne `force`**, und das ist eine Aussage: der Eingriff ist Klasse A (Nr. 42) — genau das
     * tut jeder Schlag (§22), der Figurensatz ändert sich nicht. Ein `force` anzunehmen hieße zu
     * behaupten, es gäbe hier etwas zu übergehen.
     */
    z.strictObject({ op: z.literal('POOL_PIECE'), pieceId: z.string().min(1) }),
    /**
     * Den Punktestand eines Spielers **setzen** (Zyklus 11f).
     *
     * **Ohne `force`**, und das ist eine Aussage: der Eingriff ist Klasse A (Nr. 39). Jeder Stand
     * ist zulässig, auch einer über dem Punkteziel — die Siegprüfung bleibt am Zugbeginn, und die
     * nächste Aktion beendet die Partie dann. Ein `force` anzunehmen hieße zu behaupten, es gäbe
     * hier etwas zu übergehen.
     *
     * **Ein Punktestand ist eine Zählung** (§23): ganz und nicht negativ. Das steht hier **und**
     * in der Engine — die Engine wird auch von `apps/sim` ohne diese Schicht aufgerufen.
     */
    z.strictObject({
        op: z.literal('SET_SCORE'),
        player: playerIdSchema,
        value: z.number().int().min(0),
    }),
    /**
     * Die verbleibende **Bedenkzeit** eines Spielers setzen (Zyklus 11f).
     *
     * **Mit `force`:** herabsetzen ist frei, über `timeBudgetMs` hinaus ist es Klasse B (Nr. 43) —
     * in Variante B entscheidet die Uhr die Partie (§26). Gesetzt wird der **Stand**, nie das
     * Budget: das steht im RuleConfig-Snapshot und gehört in die Partieerstellung.
     *
     * Millisekunden wie überall im Rechenkern (`timer.ts`) — der Host stellt Minuten ein, dort wo
     * die Partie entsteht.
     */
    z.strictObject({
        op: z.literal('SET_REMAINING'),
        player: playerIdSchema,
        remainingMs: z.number().int().min(0),
        force: z.boolean().optional(),
    }),
    /**
     * Die **aktive Person** setzen (Zyklus 11f).
     *
     * **Mit `force`:** auf einen Ausgeschiedenen zu setzen ist Klasse B (Nr. 44). Die
     * Zugreihenfolge bleibt dabei unberührt — ein Eingriff rückt sie nicht weiter (Nr. 37), und
     * eine Reihenfolge zu setzen gibt es nicht.
     */
    z.strictObject({
        op: z.literal('SET_ACTIVE_PLAYER'),
        player: playerIdSchema,
        force: z.boolean().optional(),
    }),
    /**
     * Einen Zug **im Namen eines Spielers** ausführen (Zyklus 11g).
     *
     * **Der einzige Eingriff, der ein echter Zug ist** (Nr. 37, ausdrücklich als Ausnahme
     * genannt): er läuft durch den normalen Commit-Weg, mit Zugnummer, Zugreihenfolge, Uhr und
     * seinen gewöhnlichen Ereignissen — nur mit Debug-Markierung. Er hält die Partie deshalb auch
     * **nicht** an; das tun die Eingriffe, die keine Züge sind.
     *
     * **Ohne `force`, und das ist eine Aussage:** ein simulierter Zug ist **legal oder gar nicht**.
     * Regelwidriges entsteht über den Zustandseditor (`MOVE_PIECE` und die übrigen) — ein
     * erzwungener „Zug" hinterließe ein `MOVE`-Ereignis, das keinen legalen Zug beschreibt, und
     * damit eine Historie, der man nicht mehr glauben kann.
     *
     * **`player` muss am Zug sein.** Wer einen anderen ziehen lassen will, setzt erst die aktive
     * Person (`SET_ACTIVE_PLAYER`) — §60 führt beides als eigene Punkte auf, und ein Zug außer der
     * Reihe wäre keine Simulation, sondern eine zweite Regel.
     *
     * Die `actionId` stammt aus den legalen Aktionen der Partie; die Debug-Ansicht
     * (`GET /api/v1/debug/games/:id`) führt sie für den Spieler am Zug auf.
     */
    z.strictObject({
        op: z.literal('SIMULATE_MOVE'),
        player: playerIdSchema,
        actionId: z.string().min(1),
    }),
    /**
     * Die Partie fortsetzen (Nr. 37).
     *
     * Kein Eingriff in den Zustand, aber der Gegenpart zu allen: der erste schreibende Eingriff
     * hält die Partie an, und „fortgesetzt wird ausdrücklich". Ohne diesen Weg bliebe eine
     * angefasste Partie für immer angehalten — und in `PAUSED` sieht nach Nr. 26 niemand das Brett.
     */
    z.strictObject({ op: z.literal('RESUME') }),
]);
/**
 * Was ein Eingriff bewirkt hat.
 *
 * **`applied: false` ist kein Fehler**, sondern der wirkungslose Fall: eine Figur auf ihr
 * eigenes Feld. Dieselbe Entscheidung wie in der Aufstellung (Nr. 32) — ein abgebrochener
 * Zieh-Vorgang ist kein Regelverstoß. Dann gibt es auch nichts zu speichern und entsprechend
 * keine Ereignisnummer.
 *
 * **`overridden` ist die Quittung für Nr. 38.** Nicht leer heißt: die Partie trägt ab jetzt
 * dauerhaft „regelwidrig erzeugt" (**E39**). Die Codes kommen aus der Engine und werden hier
 * nicht abgeschrieben — eine zweite Liste wäre eine zweite Wahrheit.
 *
 * **`status` steht dabei, weil er sich durch den Eingriff ändert** (Nr. 37: der erste
 * schreibende hält an). Wer das Ergebnis liest, soll nicht erst nachsehen müssen, ob die Partie
 * jetzt steht.
 */
export const debugInterventionResultSchema = z.strictObject({
    gameId: gameIdSchema,
    op: z.enum([
        'MOVE_PIECE',
        'REMOVE_PIECE',
        'ADD_PIECE',
        'POOL_PIECE',
        'SET_SCORE',
        'SET_REMAINING',
        'SET_ACTIVE_PLAYER',
        'SIMULATE_MOVE',
        'RESUME',
    ]),
    applied: z.boolean(),
    status: gameStatusSchema,
    overridden: z.array(z.enum(DEBUG_RULE_CODES)),
    /** Die Nummer des Eingriffsereignisses in der Historie. Fehlt, wenn nichts geschah. */
    eventSeq: z.number().int().min(1).optional(),
});
//# sourceMappingURL=rest-dtos.js.map